// IA Interactive Slider — activates one ingredient slide at a time by toggling
// classes/aria (no DOM creation). Titles activate their slide, arrows cycle,
// dots jump, a mobile swipe steps like the arrows, and the theme editor's
// block-select reveals the chosen slide.
import { onSwipe } from 'ia-util-swipe';

// Arrows and dots are mobile-only (CSS); the swipe follows them.
const DESKTOP_MQ = window.matchMedia('(min-width: 1024px)');

if (!customElements.get('ia-interactive-slider')) {
  class IaInteractiveSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-ia-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-ia-slide-dot]'));
      this.prevButton = this.querySelector('[data-ia-slide-prev]');
      this.nextButton = this.querySelector('[data-ia-slide-next]');
      if (this.slides.length === 0) return;

      this.activeIndex = 0;

      this.slides.forEach((slide, index) => {
        const title = slide.querySelector('[data-ia-slide-title]');
        title?.addEventListener('click', () => this.activate(index));
      });

      this.dots.forEach((dot) => {
        const index = parseInt(dot.dataset.index || '0', 10);
        dot.addEventListener('click', () => this.activate(index));
      });

      this.prevButton?.addEventListener('click', () => this.step(-1));
      this.nextButton?.addEventListener('click', () => this.step(1));

      // Whole layout, so the polaroids, title and description all take the swipe.
      this.unbindSwipe = onSwipe(this, {
        onNext: () => this.step(1),
        onPrev: () => this.step(-1),
        enabled: () => !DESKTOP_MQ.matches,
      });

      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      // A slide can ask to be the open one from Liquid, so the right one is
      // already open before this runs. Several may ask: the first wins, and
      // activate() drops the class from the rest.
      const asked = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.activate(asked === -1 ? 0 : asked);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.unbindSwipe?.();
    }

    activate(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.activeIndex = index;

      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        slide.querySelector('[data-ia-slide-title]')?.setAttribute('aria-pressed', String(isActive));
      });

      this.dots.forEach((dot, i) => {
        const isActive = i === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', String(isActive));
      });
    }

    step(direction) {
      const count = this.slides.length;
      this.activate((this.activeIndex + direction + count) % count);
    }

    // Theme editor: reveal the slide whose block was selected.
    handleBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const slide = event.target.closest('[data-ia-slide]');
      const index = this.slides.indexOf(slide);
      if (index !== -1) this.activate(index);
    }
  }

  customElements.define('ia-interactive-slider', IaInteractiveSlider);
}
